import { useState, useEffect } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { getLocalToday } from "../../utils/helpers";
import SearchableTLSelect from "../common/SearchableTLSelect";

const today = getLocalToday();

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, disabled, type = "text", placeholder, min }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
  );
}

function ReadValue({ value }) {
  return (
    <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border min-h-[36px]">
      {value || <span className="text-gray-400">—</span>}
    </p>
  );
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 text-left text-sm font-medium text-gray-700"
      >
        {title}
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>}
    </div>
  );
}

function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel = "Confirm", danger = true }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
            Go Back
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded text-white ${
              danger ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivateWithTLDialog({ activeTLs, onConfirm, onCancel }) {
  const [selectedTLId, setSelectedTLId] = useState(null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-2">Activate Job Order</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select a Team Leader before activating this Job Order.
        </p>
        <label className="block text-sm font-medium mb-1">
          Assigned TL <span className="text-red-500">*</span>
        </label>
        <div className="mb-4">
          <SearchableTLSelect
            options={activeTLs}
            value={selectedTLId}
            onChange={setSelectedTLId}
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
            Go Back
          </button>
          <button
            onClick={() => onConfirm(selectedTLId)}
            disabled={!selectedTLId}
            className="px-4 py-2 text-sm rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            Yes, Activate
          </button>
        </div>
      </div>
    </div>
  );
}

export default function XlsxImportDetailModal({ recordId, onClose, onRefresh, recordPatch }) {
  const { canEditXlsx, canActivateStaging, canCancelXlsx } = useAuth();
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [activeTLs, setActiveTLs] = useState([]);

  useEffect(() => {
    api.get("/team-leaders/active").then(({ data }) => setActiveTLs(data)).catch(() => {});
  }, []);

  // Merge external patches (e.g. from map modal saving coordinates)
  useEffect(() => {
    if (recordPatch) setRecord((prev) => prev ? { ...prev, ...recordPatch } : prev);
  }, [recordPatch]);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/xlsx-imports/${recordId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setRecord(data);
          setForm({
            jo_number: data.jo_number || "",
            job_date: data.job_date || today,
            account_number: data.account_number || "",
            customer_full_name: data.customer_full_name || "",
            customer_mobile_number: data.customer_mobile_number || "",
            customer_alt_mobile_number: data.customer_alt_mobile_number || "",
            municipality: data.municipality || "",
            barangay: data.barangay || "",
            plan: data.plan || "",
            complete_address: data.complete_address || "",
            assigned_tl_id: data.assigned_tl_id || null,
            assigned_tl_name: data.assigned_tl_name || "",
            team_leader_name: data.team_leader_name || "",
            notes: data.notes || "",
          });
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [recordId]);

  const isCancelled = record?.status === "Cancelled";
  const isReadOnly = isCancelled || !canEditXlsx;

  const set = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const buildPayload = () => {
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      payload[k] = v === "" ? null : v;
    });
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.put(`/xlsx-imports/${recordId}`, buildPayload());
      setRecord(data);
      onRefresh();
      setMessage({ type: "success", text: "Staging record saved successfully." });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (teamLeaderId) => {
    setActivating(true);
    setMessage(null);
    setConfirmActivate(false);
    try {
      await api.put(`/xlsx-imports/${recordId}`, buildPayload());
      await api.post(`/xlsx-imports/${recordId}/activate`, { team_leader_id: teamLeaderId });
      onRefresh();
      onClose();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Activation failed." });
    } finally {
      setActivating(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setMessage(null);
    setConfirmCancel(false);
    try {
      const { data } = await api.post(`/xlsx-imports/${recordId}/cancel`);
      setRecord(data);
      onRefresh();
      setMessage({ type: "success", text: "Staging record cancelled." });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Cancel failed." });
    } finally {
      setCancelling(false);
    }
  };

  // Helper: read-only value from record
  const rv = (field) => record?.[field] || null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Staging Record Detail</h2>
            {record && (
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded mt-1 inline-block ${
                  isCancelled ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {record.status}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-10 text-gray-400">Loading...</div>
          ) : (
            <>
              {isCancelled && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2 mb-4">
                  This record has been cancelled and is read-only.
                </div>
              )}
              {/* ── Top section (always visible, editable core fields) ── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="JO Number">
                  <Input value={form.jo_number} onChange={set("jo_number")} disabled={isReadOnly} placeholder="e.g. R3222163" />
                </Field>
                <Field label="Job Date">
                  <Input type="date" value={form.job_date} onChange={set("job_date")} disabled={isReadOnly} min={today} />
                </Field>
                <Field label="Account Number">
                  <Input value={form.account_number} onChange={set("account_number")} disabled={isReadOnly} />
                </Field>
                <Field label="Customer Full Name">
                  <Input value={form.customer_full_name} onChange={set("customer_full_name")} disabled={isReadOnly} />
                </Field>
                <Field label="Mobile Number">
                  <Input value={form.customer_mobile_number} onChange={set("customer_mobile_number")} disabled={isReadOnly} />
                </Field>
                <Field label="Alt. Mobile Number">
                  <Input value={form.customer_alt_mobile_number} onChange={set("customer_alt_mobile_number")} disabled={isReadOnly} />
                </Field>
                <Field label="Municipality">
                  <Input value={form.municipality} onChange={set("municipality")} disabled={isReadOnly} />
                </Field>
                <Field label="Barangay">
                  <Input value={form.barangay} onChange={set("barangay")} disabled={isReadOnly} />
                </Field>
                <Field label="Plan">
                  <Input value={form.plan} onChange={set("plan")} disabled={isReadOnly} />
                </Field>
                <Field label="Complete Address">
                  <Input value={form.complete_address} onChange={set("complete_address")} disabled={isReadOnly} />
                </Field>
                <Field label="Assigned TL">
                  {isReadOnly ? (
                    <ReadValue value={form.assigned_tl_name} />
                  ) : (
                    <SearchableTLSelect
                      options={activeTLs}
                      value={form.assigned_tl_id}
                      onChange={(tlId) => {
                        const tl = activeTLs.find((t) => t.id === tlId);
                        setForm((f) => ({ ...f, assigned_tl_id: tlId, assigned_tl_name: tl ? tl.name : "" }));
                      }}
                    />
                  )}
                </Field>
                <Field label="Sales Agent">
                  <Input value={form.team_leader_name} onChange={set("team_leader_name")} disabled={isReadOnly} placeholder="Full name" />
                </Field>
              </div>

              {/* ── Collapsible: Application Information ── */}
              <CollapsibleSection title="Application Information">
                <Field label="Reference ID"><ReadValue value={rv("reference_id")} /></Field>
                <Field label="Application Status"><ReadValue value={rv("application_status")} /></Field>
                <Field label="Status Updated On"><ReadValue value={rv("status_updated_on")} /></Field>
                <Field label="Application Submitted On"><ReadValue value={rv("application_submitted_on")} /></Field>
                <Field label="NAP ID"><ReadValue value={rv("nap_id")} /></Field>
                <Field label="Application Type"><ReadValue value={rv("application_type")} /></Field>
                <Field label="Source"><ReadValue value={rv("source")} /></Field>
                <Field label="Encoded By"><ReadValue value={rv("encoded_by")} /></Field>
                <Field label="Validated By"><ReadValue value={rv("validated_by")} /></Field>
                <Field label="Validator Email"><ReadValue value={rv("validator_email")} /></Field>
              </CollapsibleSection>

              {/* ── Collapsible: Customer / Personal Information ── */}
              <CollapsibleSection title="Customer / Personal Information">
                <Field label="Customer Type"><ReadValue value={rv("customer_type")} /></Field>
                <Field label="Segment"><ReadValue value={rv("segment")} /></Field>
                <Field label="Title"><ReadValue value={rv("title")} /></Field>
                <Field label="Suffix"><ReadValue value={rv("suffix")} /></Field>
                <Field label="DOB"><ReadValue value={rv("dob")} /></Field>
                <Field label="Age"><ReadValue value={rv("age")} /></Field>
                <Field label="Nationality"><ReadValue value={rv("nationality")} /></Field>
                <Field label="Notification Language"><ReadValue value={rv("notification_language")} /></Field>
                <Field label="Notification Mode"><ReadValue value={rv("notification_mode")} /></Field>
                <Field label="Designation / Position"><ReadValue value={rv("designation_position")} /></Field>
              </CollapsibleSection>

              {/* ── Collapsible: Business Information ── */}
              <CollapsibleSection title="Business Information">
                <Field label="Business Type"><ReadValue value={rv("business_type")} /></Field>
                <Field label="Business Name"><ReadValue value={rv("business_name")} /></Field>
                <Field label="TIN"><ReadValue value={rv("tin")} /></Field>
                <Field label="Industry Type"><ReadValue value={rv("industry_type")} /></Field>
                <Field label="Department"><ReadValue value={rv("department")} /></Field>
                <Field label="Company ID No."><ReadValue value={rv("company_id_no")} /></Field>
                <Field label="Employer Name"><ReadValue value={rv("employer_name")} /></Field>
                <div className="sm:col-span-2">
                  <Field label="Employer Address"><ReadValue value={rv("employer_address")} /></Field>
                </div>
              </CollapsibleSection>

              {/* ── Collapsible: Location / Address Information ── */}
              <CollapsibleSection title="Location / Address Information">
                <Field label="Province"><ReadValue value={rv("province")} /></Field>
                <Field label="Region"><ReadValue value={rv("region")} /></Field>
                <Field label="Latitude"><ReadValue value={rv("latitude")} /></Field>
                <Field label="Longitude"><ReadValue value={rv("longitude")} /></Field>
                <Field label="Home Ownership"><ReadValue value={rv("home_ownership")} /></Field>
                <Field label="Address Type"><ReadValue value={rv("address_type")} /></Field>
                <Field label="Special Economic Zone"><ReadValue value={rv("special_economic_zone")} /></Field>
                <Field label="Zipcode"><ReadValue value={rv("zipcode")} /></Field>
                <Field label="Subdivision / Village"><ReadValue value={rv("subdivision_village")} /></Field>
                <Field label="Building / Condominium"><ReadValue value={rv("building_condominium")} /></Field>
              </CollapsibleSection>

              {/* ── Collapsible: Plan / Billing / Payment Information ── */}
              <CollapsibleSection title="Plan / Billing / Payment Information">
                <Field label="Account Type"><ReadValue value={rv("account_type")} /></Field>
                <Field label="Billing Frequency"><ReadValue value={rv("billing_frequency")} /></Field>
                <Field label="Add Ons"><ReadValue value={rv("add_ons")} /></Field>
                <Field label="Promo Code"><ReadValue value={rv("promo_code")} /></Field>
                <Field label="Sales Promo Code"><ReadValue value={rv("sales_promo_code")} /></Field>
                <Field label="Payment Mode"><ReadValue value={rv("payment_mode")} /></Field>
                <Field label="Upfront Fee Payment Options"><ReadValue value={rv("upfront_fee_payment_options")} /></Field>
                <Field label="Suggested Installation Date"><ReadValue value={rv("suggested_installation_date")} /></Field>
              </CollapsibleSection>

              {/* ── Collapsible: Sales / Agency Information ── */}
              <CollapsibleSection title="Sales / Agency Information">
                <Field label="Agent Type"><ReadValue value={rv("agent_type")} /></Field>
                <Field label="Sales Agent Code"><ReadValue value={rv("sales_agent_code")} /></Field>
                <Field label="Agency Name"><ReadValue value={rv("agency_name")} /></Field>
                <Field label="TSM/KAM"><ReadValue value={rv("tsm_kam")} /></Field>
                <Field label="Referral Type"><ReadValue value={rv("referral_type")} /></Field>
              </CollapsibleSection>

              {/* ── Collapsible: Contact Information ── */}
              <CollapsibleSection title="Contact Information">
                <Field label="Landline No."><ReadValue value={rv("landline_no")} /></Field>
                <Field label="Email"><ReadValue value={rv("email")} /></Field>
                <Field label="Alternate Email"><ReadValue value={rv("alternate_email")} /></Field>
              </CollapsibleSection>

              {/* ── Collapsible: Reason / Notes ── */}
              <CollapsibleSection title="Reason / Notes">
                <div className="sm:col-span-2">
                  <Field label="Reason (raw)">
                    <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border whitespace-pre-wrap min-h-[60px]">
                      {rv("reason") || <span className="text-gray-400">—</span>}
                    </p>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Admin Notes">
                    <textarea
                      value={form.notes ?? ""}
                      onChange={(e) => set("notes")(e.target.value)}
                      disabled={isReadOnly}
                      rows={3}
                      className="w-full border rounded px-3 py-2 text-sm resize-none disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </Field>
                </div>
              </CollapsibleSection>
            </>
          )}
        </div>

        {/* Message bar */}
        {message && (
          <div className="px-6 py-2 shrink-0">
            <p className={`text-sm rounded px-3 py-2 ${
              message.type === "error"
                ? "text-red-700 bg-red-50"
                : "text-green-700 bg-green-50"
            }`}>{message.text}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t flex flex-wrap gap-2 justify-end">
          {!isCancelled && (
            <>
              {canEditXlsx && (
                <button
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
              {canActivateStaging && (
                <button
                  onClick={() => {
                    // If Assigned TL is already set, activate directly without asking
                    if (form.assigned_tl_id) {
                      handleActivate(form.assigned_tl_id);
                    } else {
                      setConfirmActivate(true);
                    }
                  }}
                  disabled={activating || loading}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {activating ? "Activating..." : "Make Job Order Active"}
                </button>
              )}
              {canCancelXlsx && (
                <button
                  onClick={() => setConfirmCancel(true)}
                  disabled={cancelling || loading}
                  className="px-4 py-2 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                >
                  {cancelling ? "Cancelling..." : "Cancel Job Order"}
                </button>
              )}
            </>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
            Close
          </button>
        </div>
      </div>

      {confirmActivate && (
        <ActivateWithTLDialog
          activeTLs={activeTLs}
          onConfirm={handleActivate}
          onCancel={() => setConfirmActivate(false)}
        />
      )}

      {confirmCancel && (
        <ConfirmDialog
          title="Cancel Job Order"
          message="Are you sure you want to cancel this Job Order? The record will remain in the staging table but cannot be activated."
          confirmLabel="Yes, Cancel"
          onConfirm={handleCancel}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
}
